import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Row, Badge } from '../ui-kit';
import { IconSpinner, IconSearch, IconShieldHalved, IconHandshake, IconPager, IconEnvelope } from '../ui-kit/icons';
import { debounce } from 'lodash';
import axios from 'axios';
import './SearchBar.css';

const icon = {
  'shield-halved': <IconShieldHalved />,
  'handshake-o': <IconHandshake />,
  handshake: <IconHandshake />,
  pager: <IconPager />,
  envelope: <IconEnvelope />
};

const ActionButtons = ({ result }) => (
  <div className="ms-3 action-buttons">
    {result.action_links.map((link, index) => (
      <a key={index} href={link.url} className="me-2" target="_blank" title={link.title} rel="noreferrer">
        {icon[link.icon]}
      </a>
    ))}
  </div>
);

const SearchBar = ({ query }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [queryValue, setQueryValue] = useState(query || '');
  const [searching, setSearching] = useState(false);

  const cancelToken = useRef(null);

  const search = query => {
    setSearching(true);

    if (cancelToken.current) {
      // abort in-progress requests
      cancelToken.current.cancel();
    }

    cancelToken.current = axios.CancelToken.source();

    axios
      .request({
        url: `/api/v2/search?q=${query}`,
        method: 'GET',
        timeout: 2000,
        cancelToken: cancelToken.current.token
      })
      .then(response => {
        setSearchResults(response.data);
        setSearching(false);
      })
      .catch(error => {
        setTimeout(() => !axios.isCancel(error) && search(query), 200); //try again
      });
  };

  // perform a search if the query was passed as a param to this component
  useEffect(() => {
    if (query && query.length > 0) search(query);
  }, []);

  const searchDebounced = useCallback(debounce(search, 200), []);

  const renderMatchContent = content => {
    // the part that matches the query is surrounded with with <?> </?>
    const [before, rest] = content.split('<?>');
    const [match, after] = rest.split('</?>');

    return (
      <>
        {before}
        <span style={{ color: 'black', backgroundColor: 'yellow' }}>{match}</span>
        {after}
      </>
    );
  };

  const renderField = field => {
    // no need to show ID if there is no match on it
    if (field.name == 'id' && !field.values[0].match) return;

    const makeLink = (url, content) => (url ? <a href={url}>{content}</a> : content);

    // comma separated list of values with optional link
    const fieldValues = field.values.map((value, index) => [
      index > 0 && ', ',
      makeLink(value.url, value.match ? renderMatchContent(value.match_content) : value.value)
    ]);

    return (
      <span key={field.name} className="me-3">
        <b>{field.name_i18n}:</b> {fieldValues}
      </span>
    );
  };

  const onChange = event => {
    const query = event.target.value;
    setQueryValue(query);
    setSearching(true);
    searchDebounced(query);
  };

  return (
    <div className="search-bar">
      <Row className="mb-5">
        <div className="col-12">
          <div className="input-group">
            <span className="input-group-text">
              {!searching && <IconSearch fixedWidth />}
              {searching && <IconSpinner spin fixedWidth />}
            </span>
            <input
              autoFocus
              className="form-control form-control-lg"
              placeholder={I18n.t('system_wide_search.placeholder')}
              type="text"
              onChange={onChange}
              value={queryValue}
            />
          </div>
        </div>
      </Row>
      <Row>
        <div className="col-12">
          {!searching && queryValue.length > 0 && searchResults.length === 0 && I18n.t('no_results_found')}
          {searchResults.map(result => (
            <div className="mb-4 search-result" key={`${result.type}_${result.id}`}>
              <h4>
                <a href={result.url}>
                  <Badge variant="secondary">{result.type_i18n}</Badge> {result.title}
                </a>
                <ActionButtons result={result} />
              </h4>
              {result.fields.map(f => renderField(f))}
            </div>
          ))}
        </div>
      </Row>
    </div>
  );
};

export default SearchBar;
